import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import CityModal from './CityModal';
import ColombiaMap from './ColombiaMap';
import MapCard from './MapCard';

const destinations = [
  {
    id: 'bogota',
    titleKey: 'bogota_title',
    descKey: 'bogota_desc',
    image: 'https://images.unsplash.com/photo-1681145553138-816eb004b846?auto=format&fit=crop&w=1600&q=85'
  },
  {
    id: 'cartagena',
    titleKey: 'cartagena_title',
    descKey: 'cartagena_desc',
    image: '/ctg-calles4.webp'
  },
  {
    id: 'medellin',
    titleKey: 'medellin_title',
    descKey: 'medellin_desc',
    image: 'https://images.pexels.com/photos/33437205/pexels-photo-33437205.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1800'
  },
  {
    id: 'coffee',
    titleKey: 'coffee_title',
    descKey: 'coffee_desc',
    image: 'https://images.unsplash.com/photo-1768407683196-521a0764da27?auto=format&fit=crop&w=1600&q=85'
  },
  {
    id: 'cali',
    titleKey: 'cali_title',
    descKey: 'cali_desc',
    image: 'https://images.pexels.com/photos/11815582/pexels-photo-11815582.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1800'
  }
];

const Colombia = ({ hideHeading = false }) => {
  const { t } = useLanguage();
  const [selectedCityId, setSelectedCityId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCityClick = (cityId) => {
    setSelectedCityId(cityId);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedCityId(null), 300);
  };

  return (
    <>
      <section
        id="colombia"
        className="bg-[#F5F2ED] py-12 md:py-16 px-6 md:px-12 lg:px-24"
        data-testid="colombia-section"
      >
        <div className="max-w-7xl mx-auto">
          {!hideHeading && (
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-sm uppercase tracking-[0.3em] text-[#D4C2A1] font-bold mb-6 text-center"
            >
              {t('colombia_title')}
            </motion.h2>
          )}

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-2xl md:text-3xl text-[#1A2B3C] leading-relaxed text-center mb-10 max-w-4xl mx-auto"
          >
            {t('colombia_intro')}
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12 mb-10">
            {destinations.map((dest, index) => (
              <motion.div
                key={dest.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                onClick={() => handleCityClick(dest.id)}
                className="group relative h-96 overflow-hidden rounded-2xl bg-white shadow-sm transition-all duration-500 hover:shadow-xl cursor-pointer"
                data-testid={`destination-card-${dest.id}`}
              >
                <img
                  src={dest.image}
                  alt={t(dest.titleKey)}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/30 transition-opacity duration-500 group-hover:bg-black/10" />
                
                <div className="absolute inset-0 flex flex-col justify-end p-8 translate-y-4 transition-transform duration-500 group-hover:translate-y-0">
                  <h3 className="text-xl md:text-2xl font-bold text-white uppercase tracking-[0.25em] mb-2">
                    {t(dest.titleKey)}
                  </h3>
                  <p className="text-white/90 text-lg opacity-0 transition-opacity duration-500 group-hover:opacity-100 line-clamp-3">
                    {t(dest.descKey)}
                  </p>
                </div>
              </motion.div>
            ))}
            <MapCard index={destinations.length} onCityOpen={handleCityClick} />
          </div>

          <ColombiaMap />
        </div>
      </section>

      <CityModal cityId={selectedCityId} isOpen={isModalOpen} onClose={closeModal} />
    </>
  );
};

export default Colombia;
